/**
 * Spreads views placement over frames.
 *
 * Placing an item means applying its data, which rasterizes text textures and uploads them to the GPU. Doing that
 * for a whole row at once costs far more than a frame budget on low-end devices, which is what makes vertical
 * navigation stall. Owners register here and get asked to place one item at a time until the budget for the current
 * frame is spent.
 *
 * An owner is any object exposing `stepPlacement()`, which places a single item and returns whether more work is
 * left, plus a `stage` reference.
 */

export const PLACEMENT_BUDGET_MS = 4;

const owners = [];

let listeningStage = null;

const process = () => {
  if (!owners.length) {
    return;
  }

  const start = Date.now();

  do {
    // NOTE: the owner stays in the queue while it steps. Applying data can make it schedule or cancel itself
    // re-entrantly, and both of those look the queue up by identity — taking it out first made a re-entrant
    // schedule enqueue a duplicate, and a re-entrant cancel get undone by the re-queueing below
    const owner = owners[0];
    let hasMoreWork = false;

    try {
      hasMoreWork = owner.stepPlacement();
    } catch (error) {
      hasMoreWork = false;
      // Dropping the owner here leaves its row half placed, so this must never pass silently
      // eslint-disable-next-line no-console
      console.error('[placement] Failed to place a view, its row is left incomplete', error);
    }

    if (owners[0] === owner) {
      owners.shift();

      // NOTE: re-queueing at the back so a long row could not starve the others
      if (hasMoreWork) {
        owners.push(owner);
      }
    }
  } while (owners.length && Date.now() - start < PLACEMENT_BUDGET_MS);
};

/**
 * Registers an owner to be drained on the upcoming frames. Calling it for an already registered owner does nothing,
 * so an owner may safely re-queue itself while its own placement is in progress.
 *
 * @param {Object} owner an object exposing `stepPlacement()` and `stage`
 */
export const schedulePlacement = owner => {
  if (!owner || owners.includes(owner)) {
    return;
  }

  owners.push(owner);

  if (!listeningStage && owner.stage) {
    listeningStage = owner.stage;
    // Placing on a frame start makes the items show up within the very same frame
    listeningStage.on('frameStart', process);
  }
};

/**
 * How many owners are still waiting to place items. Exposed for the performance monitor.
 *
 * @returns {Number} the queue depth
 */
export const getScheduledCount = () => owners.length;

/**
 * Drops any pending placement of an owner, i.e. when it went off screen or got detached
 *
 * @param {Object} owner a previously scheduled owner
 */
export const cancelPlacement = owner => {
  const index = owners.indexOf(owner);

  if (index >= 0) {
    owners.splice(index, 1);
  }
};
